import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, X } from 'lucide-react';
import api from '../services/api';
import JobForm from './JobForm';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const JobList = () => {
    const [jobs,         setJobs]         = useState([]);
    const [isLoading,    setIsLoading]    = useState(false);
    const [searchTerm,   setSearchTerm]   = useState('');
    const [pagination,   setPagination]   = useState({ page:1, limit:20, total:0, pages:0 });
    const [showForm,     setShowForm]     = useState(false);
    const [selectedJobId,setSelectedJobId]= useState(null);

    useEffect(() => { loadJobs(); }, [pagination.page]);

    const loadJobs = async () => {
        try {
            setIsLoading(true);
            const res = await api.get(`/JobGeneration.php?page=${pagination.page}&limit=${pagination.limit}`);
            if (res.data.success) { setJobs(res.data.data.jobs); setPagination(res.data.data.pagination); }
            else throw new Error(res.data.message || 'Failed to load jobs');
        } catch (err) { alert('Failed to load jobs: ' + (err.response?.data?.message || err.message)); }
        finally { setIsLoading(false); }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchTerm.trim()) { loadJobs(); return; }
        try {
            setIsLoading(true);
            const res = await api.get(`/JobGeneration.php?action=search&search=${encodeURIComponent(searchTerm)}`);
            if (res.data.success) setJobs(res.data.data.jobs);
            else throw new Error(res.data.message || 'Search failed');
        } catch (err) { alert('Search failed: ' + (err.response?.data?.message || err.message)); }
        finally { setIsLoading(false); }
    };

    const handleNewJob  = () => { setSelectedJobId(null); setShowForm(true); };
    const handleEditJob = (id) => { setSelectedJobId(id); setShowForm(true); };
    const handleCloseForm = () => { setShowForm(false); setSelectedJobId(null); };
    const handleSaved     = () => { setShowForm(false); setSelectedJobId(null); loadJobs(); };
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '';
    const handlePageChange = (p) => setPagination(prev => ({ ...prev, page: p }));

    if (showForm) return <JobForm jobId={selectedJobId} onClose={handleCloseForm} onSaved={handleSaved} />;

    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <style>{`@keyframes spin{to{transform:rotate(360deg)}} .jl-tr:hover td{background:#f9fafb;} .jl-sub:hover td{background:#DBEAFE !important;}`}</style>

            {/* Header */}
            <div className="flex justify-between items-center px-6 py-6 max-[768px]:flex-col max-[768px]:gap-4 max-[768px]:items-stretch"
                style={{ background:`linear-gradient(135deg,${PRIMARY},${PRIMARY_DARK})`, color:'#fff' }}>
                <h1 className="m-0 text-[28px] font-semibold">Job Management</h1>
                <button onClick={handleNewJob}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white border-0 rounded-md text-sm font-semibold cursor-pointer hover:bg-[#EFF6FF] transition-colors duration-150"
                    style={{ color: PRIMARY }}>
                    <Plus size={20} /> New Job
                </button>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-5 bg-gray-50 border-b border-[#E2E8F0]">
                <form onSubmit={handleSearch} className="flex gap-3 max-w-[600px] max-[768px]:flex-col">
                    <div className="relative flex-1">
                        <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input type="text" placeholder="Search by job code, MBL, HBL, customer..."
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-9 py-2.5 border border-[#CBD5E1] rounded-md text-sm outline-none focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] box-border" />
                        {searchTerm && (
                            <button type="button" onClick={() => { setSearchTerm(''); loadJobs(); }}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 bg-gray-100 border-0 rounded cursor-pointer hover:bg-gray-200 flex items-center">
                                <X size={16} className="text-gray-500" />
                            </button>
                        )}
                    </div>
                    <button type="submit"
                        className="flex items-center gap-2 px-4 py-2.5 text-white border-0 rounded-md text-sm font-medium cursor-pointer transition-colors duration-150"
                        style={{ backgroundColor: '#64748B' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#475569'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#64748B'; }}>
                        Search
                    </button>
                </form>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-4">
                    <span className="w-10 h-10 border-4 border-gray-200 border-t-[#2563EB] rounded-full block" style={{ animation:'spin 0.8s linear infinite' }} />
                    <p className="m-0 text-sm">Loading jobs...</p>
                </div>
            ) : (<>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead className="bg-[#F1F5F9] border-b-2 border-[#E2E8F0]">
                            <tr>
                                {['Job Code','Date','Job Type','Customer','Load Port','Destination','Invoice No','Invoice Date','Actions'].map(h => (
                                    <th key={h} className="px-4 py-3.5 text-left font-semibold text-gray-700 text-[13px] uppercase tracking-[0.5px]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.length === 0 ? (
                                <tr><td colSpan={9} className="text-center py-16 text-gray-400 italic text-sm">No jobs found. Click "New Job" to create one.</td></tr>
                            ) : jobs.map(job => {
                                const isSubJob = job.StatusSub===1 || job.MainJobID>0;
                                return (
                                    <tr key={job.JobID} className={`border-b border-gray-100 ${isSubJob ? 'jl-sub' : 'jl-tr'}`}
                                        style={isSubJob ? { background:'#EFF6FF' } : {}}>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">
                                            {isSubJob && <span className="inline-block mr-2 text-gray-500 text-base font-bold">↳</span>}
                                            <span className="font-semibold" style={{ color: PRIMARY }}>{job.JobCode}</span>
                                            {isSubJob && job.MainJobCode && (
                                                <div className="mt-1"><small className="text-[11px] text-gray-500 font-medium">Main: {job.MainJobCode}</small></div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">{formatDate(job.JobDate)}</td>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">{job.JobDescription}</td>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">{job.InvoiceToName}</td>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">{job.LoadPort}</td>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">{job.DestinationPort}</td>
                                        <td className="px-4 py-3.5 text-sm">
                                            {job.InvoiceNo && (
                                                <span className="inline-block px-2.5 py-1 rounded-xl text-xs font-medium text-white" style={{ backgroundColor: '#16A34A' }}>{job.InvoiceNo}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5 text-sm text-gray-900">{formatDate(job.InvoiceDate)}</td>
                                        <td className="px-4 py-3.5">
                                            <button onClick={() => handleEditJob(job.JobID)} title="Edit Job"
                                                className="p-1.5 border-0 bg-transparent cursor-pointer rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all duration-150">
                                                <Edit size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {pagination.pages > 1 && (
                    <div className="flex justify-center items-center gap-4 px-5 py-5 border-t border-[#E2E8F0]">
                        <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1}
                            className="flex items-center gap-1.5 px-3.5 py-2 text-white border-0 rounded-md text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                            style={{ backgroundColor: '#64748B' }}
                            onMouseEnter={(e) => { if (pagination.page !== 1) e.currentTarget.style.backgroundColor = '#475569'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#64748B'; }}>
                            Previous
                        </button>
                        <span className="text-sm text-gray-500">
                            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                        </span>
                        <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.pages}
                            className="flex items-center gap-1.5 px-3.5 py-2 text-white border-0 rounded-md text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                            style={{ backgroundColor: '#64748B' }}
                            onMouseEnter={(e) => { if (pagination.page !== pagination.pages) e.currentTarget.style.backgroundColor = '#475569'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#64748B'; }}>
                            Next
                        </button>
                    </div>
                )}
            </>)}
        </div>
    );
};

export default JobList;